import {parseContactLabelIds} from '@/shared'
import type {Contact} from '@/types'

/**
 * Resolve the deduplicated union of directly-selected contact wxids and
 * contacts whose labels intersect the selected label IDs.
 *
 * Order: direct wxids first (preserving input order), then label-expanded
 * wxids (preserving contacts iteration order), first-seen wins for dedup.
 *
 * Properties:
 * - Direct wxids are preserved verbatim even if no matching Contact row exists (Property 1a).
 * - Stale label IDs (not present in any contact) contribute zero wxids, no error (Property 2).
 * - Each wxid appears at most once in the result (Property 1).
 */
export function resolveTargets({
    contactWxids,
    labelIds,
    contacts,
}: {
    contactWxids: string[]
    labelIds: number[]
    contacts: Contact[]
}): string[] {
    const seen = new Set<string>()
    const result: string[] = []

    // (a) Preserve direct wxids verbatim, in order, deduplicated
    for (const wxid of contactWxids) {
        if (seen.has(wxid)) continue
        seen.add(wxid)
        result.push(wxid)
    }

    // (b) Expand labels: for each contact whose parsed labelIds intersects
    // the selected labelIds, include their wxid
    if (labelIds.length > 0) {
        const labelIdSet = new Set(labelIds)
        for (const contact of contacts) {
            if (seen.has(contact.wxid)) continue
            const contactLabels = parseContactLabelIds(contact.labelIds)
            if (contactLabels.some((id) => labelIdSet.has(id))) {
                seen.add(contact.wxid)
                result.push(contact.wxid)
            }
        }
    }

    return result
}
